import { useState, useRef, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Search } from 'lucide-react'
import { clsx } from 'clsx'

interface DropdownOption {
  value: string
  label: string
}

interface DropdownProps {
  value: string
  onChange: (value: string) => void
  options: DropdownOption[]
  placeholder?: string
  className?: string
  size?: 'sm' | 'md'
  /** Zobrazit vyhledávací pole. Výchozí: automaticky u 8+ položek. */
  searchable?: boolean
}

export default function Dropdown({ value, onChange, options, placeholder, className, size = 'md', searchable }: DropdownProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const showSearch = searchable ?? options.length > 7

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setQ('') }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (open && showSearch) setTimeout(() => searchRef.current?.focus(), 40)
  }, [open, showSearch])

  const selected = options.find((o) => o.value === value)

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return options
    return options.filter((o) => o.label.toLowerCase().includes(needle) || o.value.toLowerCase().includes(needle))
  }, [q, options])

  const pick = (v: string) => { onChange(v); setOpen(false); setQ('') }

  return (
    <div ref={ref} className={clsx('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={clsx(
          'w-full flex items-center justify-between gap-2 border rounded-lg bg-white text-left transition',
          'border-gray-200 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
          'dark:bg-zinc-900 dark:border-white/10 dark:text-gray-200 dark:hover:border-white/20',
          size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2.5 text-sm'
        )}
      >
        <span className={clsx('truncate', !selected && 'text-gray-400')}>
          {selected?.label || placeholder || t('common.select_ph', { defaultValue: 'Vyberte…' })}
        </span>
        <ChevronDown className={clsx('w-3.5 h-3.5 text-gray-400 transition-transform shrink-0', open && 'rotate-180')} />
      </button>

      {open && (
        <div className={clsx(
          'absolute z-50 mt-1 w-full min-w-[180px] bg-white dark:bg-zinc-900 rounded-xl shadow-xl border border-gray-200 dark:border-white/10 py-1 animate-fade-in'
        )}>
          {showSearch && (
            <div className="p-1.5 border-b border-gray-100 dark:border-white/10">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  ref={searchRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t('common.search', { defaultValue: 'Hledat…' })}
                  className="w-full pl-8 pr-2 py-1.5 rounded-lg bg-gray-50 dark:bg-white/5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                />
              </div>
            </div>
          )}
          <div className="max-h-60 overflow-y-auto">
            {filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => pick(opt.value)}
                className={clsx(
                  'w-full text-left px-3 transition truncate',
                  size === 'sm' ? 'py-1.5 text-xs' : 'py-2 text-sm',
                  opt.value === value
                    ? 'bg-primary-50 text-primary-600 font-medium dark:bg-primary-500/10 dark:text-primary-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5'
                )}
                title={opt.label}
              >
                {opt.label}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-3 text-xs text-gray-400 text-center">{t('common.nothing_found', { defaultValue: 'Nic nenalezeno' })}</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
